import {
  Box,
  Disclaimer,
  Div,
  formatAmount,
  H5,
  InfoComponentMobile,
  lightTheme,
  NumberType,
  P,
  TableHeader,
} from '@vgrubs/components';

/** One location's vTablet charges per order or per day, in cents. */
export type SaasLocationCharges = {
  locationId: number;
  locationName: string;
  /** The amount set NOW; null = no longer billed this way. */
  fee: number | null;
  /** Orders, or daily charges. */
  count: number;
  amount: number;
};

type Props = {
  unit: 'order' | 'day';
  locations: SaasLocationCharges[];
};

const COPY = {
  order: { count: 'Orders', empty: 'No per-order charges in this period.' },
  day: { count: 'Charges', empty: 'No per-day charges in this period.' },
};

const COLUMNS = (unit: Props['unit']) => [
  { label: 'Location', width: '40%' },
  { label: 'Fee', width: '20%' },
  { label: COPY[unit].count, width: '16%' },
  { label: 'Collected', width: '18%' },
];

const [LOCATION, FEE, COUNT, COLLECTED] = COLUMNS('order').map(
  (column) => column.width
);

const openLocation = (locationId: number) =>
  window.open(`/locations/${locationId}`, '_blank', 'noopener,noreferrer');

/**
 * The vTablet fee charged per order or per day in the range, one line per
 * location, biggest first (as the backend sorts them). A location that moved
 * between the two in the range is in both lists, each with its own part.
 */
export const SaasLocationChargesSection = ({ unit, locations }: Props) => {
  if (locations.length === 0)
    return (
      <Div $flex $center $height="100px">
        <P $m="0" $color={lightTheme.lightGray}>
          {COPY[unit].empty}
        </P>
      </Div>
    );

  return (
    <>
      <TableHeader columns={COLUMNS(unit)} padding="0 16px" gap="12px" />
      {locations.map((location) => {
        const collected = formatAmount(location.amount, NumberType.USD);
        // The fee set NOW, so "-" for a location billed another way since,
        // even though it was charged this way in the range.
        const fee = (
          <P
            $m="0"
            $color={location.fee == null ? lightTheme.lightGray : undefined}
          >
            {location.fee == null
              ? '-'
              : `${formatAmount(location.fee, NumberType.USD)} / ${unit}`}
          </P>
        );

        return (
          <Div key={location.locationId}>
            <Div
              $desktop
              $flex
              $alignCenter
              $gap="12px"
              $p="12px 16px"
              $borderBottom
              $borderColor={lightTheme.mediumGray}
            >
              <Div $width={LOCATION}>
                <P
                  $m="0"
                  $pointer
                  $hoverUnderline
                  onClick={() => openLocation(location.locationId)}
                >
                  {location.locationName}
                </P>
              </Div>
              <Div $width={FEE}>{fee}</Div>
              <Div $width={COUNT}>
                <P $m="0">{location.count}</P>
              </Div>
              <Div $width={COLLECTED}>
                <P $m="0" $color={lightTheme.green}>
                  {collected}
                </P>
              </Div>
            </Div>

            <Box
              $mobile
              $flex
              $column
              $gap="4px"
              $mb="8"
              $p="12px 16px"
              $pSM="12px 16px"
              style={{ boxShadow: 'none' }}
            >
              <Div $flex $alignStart $between $gap="8px">
                <H5
                  $m="0"
                  $pointer
                  onClick={() => openLocation(location.locationId)}
                >
                  {location.locationName}
                </H5>
                <H5 $m="0" $noWrap $color={lightTheme.green}>
                  {collected}
                </H5>
              </Div>
              <InfoComponentMobile title="Fee" component={fee} />
              <InfoComponentMobile
                title={COPY[unit].count}
                component={<Disclaimer $m="0">{location.count}</Disclaimer>}
              />
            </Box>
          </Div>
        );
      })}
    </>
  );
};
